'use client';

import { getVisibleContentFields } from '@eliadmin/core/src/utils/content-fields';
import { contentFieldKey } from '@eliadmin/core/src/api/content-fields/public-content-fields';
import { ContentFieldBlock } from '@eliadmin/core/src/components/content-fields/content-fields';
import { ProductTabsDescription } from '@eliadmin/core/src/components/product/detail/tabs/product-tabs-description';
import { ProductTabsReviews } from '@eliadmin/core/src/components/product/detail/tabs/product-tabs-reviews';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';

import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';

type TabDef = {
  id: string;
  name: string;
  content: React.ReactNode;
  show: boolean;
};

export function ProductTabs() {
  const locale = useLocaleStore(s => s.currentLocale);
  const product = useVariantsStore(state => state.product);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const desiredObject = product.variants?.length ? selectedVariant : product;

  const tabs = useMemo<TabDef[]>(
    () => [
      {
        id: 'description',
        name: 'Popis a parametre',
        content: <ProductTabsDescription product={product} />,
        show: !!selectedVariant?.description || !!product?.description,
      },
      {
        id: 'reviews',
        name: 'Recenzie',
        content: <ProductTabsReviews product={product} locale={locale} />,
        show: true,
      },

      ...getVisibleContentFields(product).map(field => ({
        id: contentFieldKey('products', field.id),
        key: contentFieldKey('products', field.id),
        name: field.name,
        title: field.name,
        content: <ContentFieldBlock field={field} module="products" />,
        show: true,
      })),
    ],
    [product, selectedVariant, desiredObject?.description, locale],
  );

  const visibleTabs = useMemo(() => tabs.filter(tab => tab.show), [tabs]);

  const [activeTab, setActiveTab] = useState<TabDef['id']>('description');

  useEffect(() => {
    const firstId = visibleTabs[0]?.id;
    if (!firstId) return;

    const isActiveStillVisible = visibleTabs.some(t => t.id === activeTab);
    if (!isActiveStillVisible) setActiveTab(firstId);
  }, [visibleTabs, activeTab]);

  if (visibleTabs.length === 0) return null;

  return (
    <section
      className={cn(
        'custom-section',
        'relative pb-10 pt-16 sm:pb-[50px] sm:pt-32 md:pt-40 lg:pt-[200px]',
      )}
    >
      <Image
        src={'/images/subpage-header-vector.svg'}
        alt="Tabs"
        width={0}
        height={0}
        className="absolute left-0 top-0 z-10 h-auto w-full"
      />
      <Tabs
        value={activeTab}
        onValueChange={v => setActiveTab(v as TabDef['id'])}
        className={cn(
          'custom-container',
          'flex flex-col items-center justify-center gap-6 sm:gap-8 md:gap-10 lg:gap-[50px]',
        )}
      >
        <TabsList className="grid h-fit w-full grid-cols-2 rounded-none bg-transparent p-0 shadow-none">
          {visibleTabs.map(tab => (
            <TabsTrigger
              key={tab.id}
              value={tab.id}
              className={cn(
                'ea-product-tabs__trigger',
                'col-span-1 flex h-12 w-full items-center justify-center rounded-none border-b-2 text-sm font-medium shadow-none sm:h-[50px] sm:text-base',
              )}
            >
              {tab.name}
            </TabsTrigger>
          ))}
        </TabsList>
        {visibleTabs.map(tab => (
          <TabsContent key={tab.id} className="w-full" value={tab.id}>
            {tab.content}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
